import { defineProvider } from '@saraudio/core';
import type { Logger } from '@saraudio/utils';
import { resolveConfig } from './config';
import { SonioxOptionsSchema } from './schema';
import { transcribeHTTP } from './transport-http';
import { createWsStream } from './transport-ws';
import type { SonioxOptions, SonioxProvider } from './types';

export function soniox(options: SonioxOptions): SonioxProvider {
  const validated = SonioxOptionsSchema.parse(options);
  const logger: Logger | undefined = validated.logger ? validated.logger.child('provider-soniox') : undefined;
  let resolved = resolveConfig(validated);
  const listeners = new Set<(opts: SonioxOptions) => void>();

  return defineProvider({
    id: 'soniox',
    capabilities: {
      partials: 'mutable',
      words: true,
      diarization: 'word',
      language: 'final',
      segments: false,
      forceEndpoint: true,
      multichannel: true,
      translation: 'two_way',
      transports: { http: true, websocket: true },
    },
    getPreferredFormat() {
      return { encoding: 'pcm16', sampleRate: resolved.sampleRate, channels: resolved.channels } as const;
    },
    getSupportedFormats() {
      return [
        { encoding: 'pcm16', sampleRate: 16000, channels: 1 },
        { encoding: 'pcm16', sampleRate: 16000, channels: 2 },
        { encoding: 'pcm16', sampleRate: 8000, channels: 1 },
      ] as const;
    },
    negotiateFormat(candidate) {
      const sampleRate = candidate.sampleRate ?? resolved.sampleRate;
      const channels = (candidate.channels ?? resolved.channels) as 1 | 2;
      return { encoding: 'pcm16', sampleRate, channels } as const;
    },
    async update(next) {
      const validatedNext = SonioxOptionsSchema.parse(next);
      resolved = resolveConfig(validatedNext);
      listeners.forEach((l) => l(validatedNext));
    },
    onUpdate(listener) {
      listeners.add(listener);
      return () => listeners.delete(listener);
    },
    stream() {
      return createWsStream(resolved, logger);
    },
    async transcribe(audio, batchOptions, signal) {
      return transcribeHTTP(resolved, audio, batchOptions, signal, logger);
    },
  });
}
